import { useCallback, useMemo, useRef, useState } from "react";
import { Box, Container, Drawer, ScrollArea, Space, useMantineTheme } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Introduction } from "./Introduction";
import { UserInput } from "../../components/user-input";
import { ExampleQuestions } from "../../components/example-questions";
import { TopNav } from "../../components/top-nav";
import { useConfigs } from "../../hooks/useConfigs";
import { useChatApi } from "../../hooks/useChatApi";
import classes from "./Browse.module.css";
import { useSettings } from "../../SettingsContext";
import { V2ChatHistoryApiResponse, V2Source } from "../../api";
import { Conversation } from "../../components/conversation";
import { isInternalEmployee, useLogin } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { SourceViewer } from "../../components/sourceviewer";
import { useCountry } from "../../CountryContext";
import { useDomain, Domain } from "../../DomainContext";
import { getChatError } from "../../utils/chatError";

export function Browse() {
    const [opened, { toggle }] = useDisclosure(false);
    const lastQuestionRef = useRef<string>("");
    const client = useLogin ? useMsal().instance : undefined;
    const { t } = useTranslation();

    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [selectedSource, setSelectedSource] = useState<V2Source | undefined>(undefined);
    const [selectedSourceAnchor, setSelectedSourceAnchor] = useState<string>();

    const theme = useMantineTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
    const drawerWidth = useMemo(() => (isMobile ? "100%" : "80%"), [isMobile]);
    const mainContentStyle = useMemo(
        () => ({
            marginRight: opened ? drawerWidth : 0
        }),
        [opened, isMobile, drawerWidth]
    );
    const { country } = useCountry();
    const { domain, setDomain } = useDomain();
    const configs = useConfigs();
    const { clearChat, error, isLoading, isStreaming, makeApiRequest, parsedStreamedAnswer, parsedAnswers, handleOnHistoryItemClick, conversationId } =
        useChatApi();
    const [_, dispatch] = useSettings();
    const chatError = useMemo(() => (error ? getChatError(error, t) : undefined), [error, t]);

    const isAnyQuestionAsked = useMemo(() => {
        return !!lastQuestionRef.current;
    }, [lastQuestionRef.current]);

    const onSourceClick = useCallback(async (source: V2Source, anchor: string) => {
        if (source.dataSource === "playbook") {
            window.open(source.url, "_blank");
        }
        if (source.dataSource === "pm360") {
            window.open(source.url, "_blank");
        } else if (source.dataSource == "orbit" && client && !(await isInternalEmployee(client))) {
            window.open(source.url, "_blank");
        } else if (source.dataSource == "intellum") {
            window.open(source.url, "_blank");
        } else if (source.dataSource === "webpage") {
            window.open(source.url, "_blank");
        } else if (source.dataSource === "taxonomy_item") {
            window.open(source.url, "_blank");
        } else {
            toggle();
            setSelectedSource(source);
            setSelectedSourceAnchor(anchor);
        }
    }, []);

    const onNewConversationClick = async () => {
        await onStop();
        lastQuestionRef.current = "";
        clearChat();
    };

    // A conversation is bound to a single domain (the assistant's identity, capabilities
    // and prompt differ per domain), so toggling the selector starts a fresh conversation.
    const onDomainChange = async (newDomain: Domain) => {
        if (newDomain === domain) return;
        await onNewConversationClick();
        setDomain(newDomain);
    };

    const onSend = (question: string, selectedAssetTypes: string[], selectedInterestAreas: string[], sstRequestIds: string[] = []) => {
        lastQuestionRef.current = question;

        const controller = new AbortController();
        setAbortController(controller);

        // Scrolling is handled by Conversation, which anchors the new question to
        // the top of the viewport when the turn starts.
        makeApiRequest(question, selectedAssetTypes, selectedInterestAreas, country, domain, sstRequestIds, controller.signal);
    };

    // Keep a stable identity for handlers passed into the memoized conversation list;
    // a fresh closure per render would force every turn to re-render on each
    // streaming update.
    const onSendRef = useRef(onSend);
    onSendRef.current = onSend;
    const onFollowupQuestionClick = useCallback((question: string) => onSendRef.current(question, [], []), []);

    const onStop = async () => {
        try {
            if (abortController) {
                abortController.abort();
            }
        } catch (e) {}
    };

    const onChatHistoryItemClick = (item: V2ChatHistoryApiResponse) => {
        handleOnHistoryItemClick(item);
        // Reopening a conversation resumes it, so the composer must show the domain
        // its most recent turn was asked in.
        const lastTurn = item.answers[item.answers.length - 1];
        setDomain(lastTurn.domain);
        lastQuestionRef.current = lastTurn.userMessage;
    };

    return (
        <Container size={opened ? undefined : "xl"} fluid={opened} className={classes.container} style={{ height: "calc(100vh - 6rem)", width: "100%" }}>
            <Box style={mainContentStyle} className={`${classes.mainContent} ${!isAnyQuestionAsked ? classes.centerVertically : ""}`} p="sm">
                <Box className={classes.topNav}>
                    <TopNav
                        isAnyQuestionAsked={isAnyQuestionAsked}
                        conversationId={conversationId}
                        shareableContent={parsedAnswers}
                        onNewConversationClick={onNewConversationClick}
                        onSettingsChange={dispatch}
                        notifty={!isStreaming && !isLoading}
                        onChatHistoryItemClick={onChatHistoryItemClick}
                    />
                </Box>
                {isAnyQuestionAsked && (
                    <ScrollArea.Autosize
                        className={classes.scrollArea}
                        mah="calc(100vh - 22rem)"
                        maw="100%"
                        scrollHideDelay={500}
                        onWheel={event => {
                            if (opened) {
                                event.stopPropagation(); // Allow scrolling in the drawer
                            }
                        }}
                    >
                        <Conversation
                            hasError={!!error}
                            errorKind={chatError?.kind}
                            errorMessage={chatError?.message}
                            isLoading={isLoading}
                            isStreaming={isStreaming}
                            onSourceClick={onSourceClick}
                            answers={parsedAnswers}
                            streamingAnswer={parsedStreamedAnswer}
                            onFollowupQuestionClick={onFollowupQuestionClick}
                        />
                    </ScrollArea.Autosize>
                )}
                {!isAnyQuestionAsked && (
                    <>
                        <Introduction />
                        <Space h="lg" />
                    </>
                )}

                {parsedAnswers == null || parsedAnswers.length == 0 || !parsedAnswers[parsedAnswers.length - 1].response?.phiDetected ? (
                    <UserInput isStreaming={isStreaming} isLoading={isLoading} onSend={onSend} onStop={onStop} onDomainChange={onDomainChange} />
                ) : (
                    <> </>
                )}

                {!isAnyQuestionAsked && (
                    <Box className={classes.exampleQuestions}>
                        <ExampleQuestions onClick={q => onSend(q, [], [])} />
                    </Box>
                )}
            </Box>
            <Drawer
                withOverlay={true}
                opened={opened}
                onClose={toggle}
                position="right"
                size={drawerWidth}
                title={selectedSource?.title}
                withCloseButton={true}
                transitionProps={{ transition: "fade-left", duration: 150, timingFunction: "linear" }}
                styles={{
                    body: {
                        height: "calc(100vh - 7rem)"
                    }
                }}
            >
                <SourceViewer source={selectedSource} anchor={selectedSourceAnchor} />
            </Drawer>
        </Container>
    );
}
